'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { organizationService } from '@/services/organizationService';

interface FormData {
  name: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
  };
  owner: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  };
}

export default function OrganizationRegistration() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    },
    owner: {
      firstName: '',
      lastName: '',
      email: '',
      password: ''
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await organizationService.createOrganization(formData);
      toast.success('Organization registered successfully!');
      router.push(`/register/success?storeCode=${response.storeCode}`);
    } catch (error: any) {
      console.error('Registration error:', error);
      
      // Handle specific error messages
      const errorMessage = error.message || 'Failed to register organization';
      
      if (errorMessage.includes('already registered as an organization owner')) {
        toast.error('This email is already registered as an organization owner. Please use a different email or contact support.');
      } else if (errorMessage.includes('registered as a system administrator')) {
        toast.error('This email is registered as a system administrator. Please use a different email.');
      } else if (errorMessage.includes('registered as an employee')) {
        toast.error('This email is already registered as an employee. Please use a different email.');
      } else if (errorMessage.includes('Password must be')) {
        toast.error(errorMessage);
      } else {
        toast.error('Failed to register organization. Please try again or contact support.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('owner.')) {
      const ownerField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        owner: {
          ...prev.owner,
          [ownerField]: value
        }
      }));
    } else if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Register Your Organization</h1>
          <p className="text-gray-600">Create an account to start managing your tips</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Organization Name
            </label>
            <Input
              id="name"
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleChange}
              className="mt-1"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Organization Email
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="mt-1"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
              Phone Number
            </label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              required
              value={formData.phone}
              onChange={handleChange}
              className="mt-1"
            />
          </div>

          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Address Information</h2>
            
            <div>
              <label htmlFor="address.street" className="block text-sm font-medium text-gray-700">
                Street Address
              </label>
              <Input
                id="address.street"
                name="address.street"
                type="text"
                required
                value={formData.address.street}
                onChange={handleChange}
                className="mt-1"
              />
            </div>

            <div>
              <label htmlFor="address.city" className="block text-sm font-medium text-gray-700">
                City
              </label>
              <Input
                id="address.city"
                name="address.city"
                type="text"
                required
                value={formData.address.city}
                onChange={handleChange}
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="address.state" className="block text-sm font-medium text-gray-700">
                  State
                </label>
                <Input
                  id="address.state"
                  name="address.state"
                  type="text"
                  required
                  maxLength={2}
                  placeholder="NY"
                  value={formData.address.state}
                  onChange={handleChange}
                  className="mt-1 uppercase"
                />
              </div>

              <div>
                <label htmlFor="address.zipCode" className="block text-sm font-medium text-gray-700">
                  ZIP Code
                </label>
                <Input
                  id="address.zipCode"
                  name="address.zipCode"
                  type="text"
                  required
                  pattern="\d{5}(-\d{4})?"
                  placeholder="12345"
                  value={formData.address.zipCode}
                  onChange={handleChange}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <h2 className="text-lg font-semibold mb-4">Owner Information</h2>
            
            <div>
              <label htmlFor="owner.firstName" className="block text-sm font-medium text-gray-700">
                First Name
              </label>
              <Input
                id="owner.firstName"
                name="owner.firstName"
                type="text"
                required
                value={formData.owner.firstName}
                onChange={handleChange}
                className="mt-1"
              />
            </div>

            <div>
              <label htmlFor="owner.lastName" className="block text-sm font-medium text-gray-700">
                Last Name
              </label>
              <Input
                id="owner.lastName"
                name="owner.lastName"
                type="text"
                required
                value={formData.owner.lastName}
                onChange={handleChange}
                className="mt-1"
              />
            </div>

            <div className="mt-4">
              <label htmlFor="owner.email" className="block text-sm font-medium text-gray-700">
                Owner Email
              </label>
              <Input
                id="owner.email"
                name="owner.email"
                type="email"
                required
                value={formData.owner.email}
                onChange={handleChange}
                className="mt-1"
              />
            </div>

            <div className="mt-4">
              <label htmlFor="owner.password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <Input
                id="owner.password"
                name="owner.password"
                type="password"
                required
                value={formData.owner.password}
                onChange={handleChange}
                className="mt-1"
                minLength={8}
              />
              <p className="text-xs text-gray-500 mt-1">Password must be at least 8 characters long</p>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? 'Registering...' : 'Register Organization'}
          </Button>

          {isLoading && (
            <div className="text-center text-sm text-gray-600">
              Please wait while we set up your organization...
            </div>
          )}
        </form>
      </div>
    </div>
  );
} 